import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { KeyRound, Loader2, Printer, RefreshCw, ShieldAlert } from 'lucide-react';
import { apiClient } from '../../services/api';
import { API_BASE_URL } from '../../config';
import { adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

/**
 * Los accesos de caja de todas las tiendas, en una hoja para imprimir.
 *
 * Iván (13-ago-2026): «QR de todas las tiendas» y «generar todos los PINES».
 * Hasta ahora eso solo existía como comando de consola, lo que en la práctica
 * quería decir que solo yo podía repartir accesos — y quien tiene enfrente a
 * los encargados de tienda es él.
 *
 * La pantalla está pensada para lo que va a pasar de verdad con ella: se
 * imprime, se recorta y se le entrega **a cada tienda su recuadro**. Por eso
 * cada tienda es una tarjeta cerrada, con su QR, su enlace y su PIN juntos, y
 * por eso hay estilos de impresión: en papel desaparecen los botones y los
 * avisos, y queda solo lo que la tienda necesita.
 *
 * El PIN se ve **una sola vez**. En la base queda hasheado, así que si se
 * cierra esta pantalla sin imprimir, la única salida es rotarlo. Eso se dice
 * arriba, en grande, y no en una nota al pie.
 */
const AccesosDeTienda = () => {
  const [tiendas, setTiendas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  /* PIN en claro por tienda, solo en memoria y solo hasta recargar. */
  const [pines, setPines] = useState({});
  const areaImpresion = useRef(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await adminStoresAPI.getAll();
      setTiendas(res?.data ?? []);
    } catch (err) {
      toast({
        title: 'No se pudieron cargar las tiendas',
        description: err.message || 'Intenta de nuevo',
        variant: 'destructive',
      });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  /**
   * @param {boolean} rotar  true = cambia también los PIN que ya existen.
   */
  const generar = async (rotar) => {
    if (rotar && !window.confirm(
      'Se van a cambiar los PIN de TODAS las tiendas.\n\n'
      + 'Cada caja que esté abierta en este momento se cerrará y tendrá que '
      + 'escribir el PIN nuevo antes de seguir atendiendo. ¿Seguimos?'
    )) {
      return;
    }

    setGenerando(true);
    try {
      const res = await apiClient.post(
        `${API_BASE_URL}/api/admin/stores/access-pins`,
        { rotar: rotar ? 1 : 0 }
      );

      const nuevos = {};
      (res?.data?.tiendas ?? []).forEach((t) => { if (t.pin) nuevos[t.id] = t.pin; });
      setPines((prev) => ({ ...prev, ...nuevos }));

      toast({ title: res.message || 'Listo' });
      await cargar();
    } catch (err) {
      toast({
        title: 'No se pudieron generar los PIN',
        description: err.message || 'Intenta de nuevo',
        variant: 'destructive',
      });
    } finally {
      setGenerando(false);
    }
  };

  const enlaceDe = (t) => t.accessUrl
    || (t.accessSlug ? `${window.location.origin}/t/${t.accessSlug}` : null);

  const conPinALaVista = Object.keys(pines).length;

  return (
    <div className="p-8">
      <Toaster />

      {/* En papel sobra todo esto: los botones no se pueden pulsar y el aviso
          ya cumplió su función en pantalla. */}
      <style>{`
        @media print {
          .no-imprimir { display: none !important; }
          .hoja-accesos { display: block !important; }
          .tarjeta-acceso {
            break-inside: avoid;
            page-break-inside: avoid;
            border: 1px dashed #999 !important;
            margin-bottom: 14px;
          }
        }
      `}</style>

      <div className="no-imprimir">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-grad-vivo flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-vd-h2 text-vd-tinta">Accesos de tienda</h2>
              <p className="text-vd-pizarra text-sm">
                El enlace y el PIN con los que cada tienda escanea los códigos de los clientes.
              </p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => generar(false)}
              disabled={generando}
              className="rounded-full px-5 h-11 bg-black text-white font-semibold disabled:opacity-50"
            >
              {generando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generar los que faltan'}
            </button>
            <button
              type="button"
              onClick={() => generar(true)}
              disabled={generando}
              className="rounded-full px-5 h-11 bg-white border border-vd-linea text-vd-tinta font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Cambiar todos
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-full px-5 h-11 bg-white border border-vd-linea text-vd-tinta font-semibold flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Imprimir
            </button>
          </div>
        </div>

        {conPinALaVista > 0 && (
          <div className="rounded-card border-2 border-vd-tinta bg-white p-4 my-5">
            <p className="font-semibold text-vd-tinta">
              Hay {conPinALaVista} PIN a la vista. Imprime o anótalos ahora.
            </p>
            <p className="text-vd-pizarra text-sm mt-1">
              No se vuelven a mostrar: en la base solo queda su huella cifrada. Si cierras esta
              pantalla sin guardarlos, la única salida es generarlos de nuevo — y eso cierra las
              cajas que estén abiertas.
            </p>
          </div>
        )}
      </div>

      {cargando ? (
        <p className="text-vd-pizarra">Cargando tiendas…</p>
      ) : (
        <div
          ref={areaImpresion}
          className="hoja-accesos grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
        >
          {tiendas.map((t) => {
            const enlace = enlaceDe(t);
            const pin = pines[t.id];

            return (
              <div
                key={t.id}
                className="tarjeta-acceso bg-white border border-vd-linea rounded-card p-5 flex gap-4"
              >
                <div className="shrink-0">
                  {enlace ? (
                    /* El QR lleva el enlace de la tienda: el encargado lo
                       escanea con su teléfono y llega a su caja sin teclear
                       una dirección larga, que es donde se equivocan. */
                    <QRCodeCanvas value={enlace} size={104} level="M" includeMargin={false} />
                  ) : (
                    <div className="w-[104px] h-[104px] rounded-xl bg-vd-niebla flex items-center justify-center text-xs text-vd-pizarra text-center px-2">
                      sin enlace
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="font-display text-vd-h3 text-vd-tinta truncate">{t.name}</p>

                  {enlace && (
                    <p className="text-[11px] text-vd-pizarra break-all mt-1 leading-snug">{enlace}</p>
                  )}

                  <div className="mt-3">
                    {pin ? (
                      <>
                        <p className="text-[11px] uppercase tracking-widest text-vd-pizarra">PIN</p>
                        <p className="text-2xl font-bold tracking-[0.28em] text-vd-tinta">{pin}</p>
                      </>
                    ) : t.hasAccessPin ? (
                      <p className="text-sm text-vd-pizarra">
                        PIN ya configurado
                        {t.accessPinSetAt
                          && ` · desde el ${new Date(t.accessPinSetAt).toLocaleDateString('es-MX')}`}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-700 flex items-center gap-1.5">
                        <ShieldAlert className="w-4 h-4 shrink-0" />
                        Sin PIN: esta caja todavía no puede canjear
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccesosDeTienda;
